/* Phase 7C-a — Time-tracking + Recurring sections in Task Drawer. */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Play, Square, Plus, Trash2, Repeat, Clock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatISTDate, formatISTShort } from "@/lib/time";
import errorToast from "@/lib/errorToast";
import { confirm } from "@/components/ui/ConfirmDialog";

const fmtDur = (m) => {
  if (!m || m < 0) return "0m";
  const h = Math.floor(m / 60), mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

export function TimeSection({ taskId, canEdit }) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [runningLogId, setRunningLogId] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ started_at: "", stopped_at: "", note: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!taskId || taskId === "new") return;
    try {
      const { data } = await api.get(`/pm/tasks/${taskId}/time-logs`);
      setLogs(data.items || []); setTotal(data.total_minutes || 0);
      const active = (data.items || []).find(l => !l.stopped_at);
      setRunningLogId(active?.id || null);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, [taskId]);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/pm/tasks/${taskId}/timer/start`, {});
      if (data.auto_stopped_previous) toast.info(t("time.autoStopped", "Auto-stopped previous timer"));
      toast.success(t("time.started", "Timer started"));
      await load();
    } catch (e) {
      errorToast(e);
    } finally { setLoading(false); }
  };
  const stop = async () => {
    setLoading(true);
    try {
      await api.post(`/pm/tasks/${taskId}/timer/stop`);
      toast.success(t("time.stopped", "Timer stopped"));
      await load();
    } catch (e) {
      errorToast(e);
    } finally { setLoading(false); }
  };
  const submitManual = async () => {
    if (!manual.started_at || !manual.stopped_at) { toast.error(t("time.needBothTimes", "Start and end required")); return; }
    try {
      await api.post(`/pm/tasks/${taskId}/time-logs`, {
        started_at: new Date(manual.started_at).toISOString(),
        stopped_at: new Date(manual.stopped_at).toISOString(),
        note: manual.note || null, is_billable: true,
      });
      toast.success(t("time.logged", "Time logged"));
      setShowManual(false); setManual({ started_at: "", stopped_at: "", note: "" });
      await load();
    } catch (e) {
      errorToast(e);
    }
  };
  const del = async (id) => {
    if (!(await confirm({
      title: t("time.confirmDel", "Delete this time log?"),
      description: "The hours logged will be removed from timesheets and reports. Managers may lose visibility.",
      confirmLabel: "Delete",
      testIdPrefix: "time-log-delete",
    }))) return;
    try {
      await api.delete(`/pm/time-logs/${id}`);
      toast.success(t("time.deleted", "Deleted"));
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!taskId || taskId === "new") return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="time-section">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-3.5 h-3.5 text-sky-300" />
        <span className="text-xs uppercase tracking-widest text-foreground/70">{t("time.title", "Time")}</span>
        <span className="ml-auto text-[11px] text-sky-300 font-medium" data-testid="time-total">⏱ {fmtDur(total)} {t("time.logged_label", "logged")}</span>
      </div>
      <div className="flex gap-2 mb-2 flex-wrap">
        {canEdit && !runningLogId && (
          <Button size="sm" onClick={start} disabled={loading} className="bg-emerald-600 hover:bg-emerald-500" data-testid="timer-start-btn">
            <Play className="w-3 h-3 mr-1" /> {t("time.start", "Start Timer")}
          </Button>
        )}
        {canEdit && runningLogId && (
          <Button size="sm" onClick={stop} disabled={loading} className="bg-rose-600 hover:bg-rose-500 animate-pulse" data-testid="timer-stop-btn">
            <Square className="w-3 h-3 mr-1" /> {t("time.stop", "Stop Timer")}
          </Button>
        )}
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setShowManual(v => !v)} data-testid="time-manual-toggle">
            <Plus className="w-3 h-3 mr-1" /> {t("time.log", "Log Time")}
          </Button>
        )}
      </div>
      {showManual && (
        <div className="mb-2 p-2 rounded-lg border border-white/10 bg-black/20 space-y-1 text-xs" data-testid="time-manual-form">
          <div className="flex gap-2">
            <Input type="datetime-local" value={manual.started_at} onChange={e => setManual({ ...manual, started_at: e.target.value })} data-testid="time-manual-start" />
            <Input type="datetime-local" value={manual.stopped_at} onChange={e => setManual({ ...manual, stopped_at: e.target.value })} data-testid="time-manual-stop" />
          </div>
          <Input placeholder={t("time.note", "Note (optional)")} value={manual.note} onChange={e => setManual({ ...manual, note: e.target.value })} data-testid="time-manual-note" />
          <Button size="sm" onClick={submitManual} className="w-full bg-gradient-to-br from-brand-blue to-sky-500" data-testid="time-manual-submit">
            {t("time.save", "Save")}
          </Button>
        </div>
      )}
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {logs.length === 0 && <div className="text-[11px] text-foreground/40 italic px-1">{t("time.empty", "No time logged yet")}</div>}
        {logs.map(l => (
          <div key={l.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-white/5" data-testid={`time-log-${l.id}`}>
            <span className="text-sky-300 font-medium w-14">{l.stopped_at ? fmtDur(l.duration_minutes) : <span className="animate-pulse text-emerald-300">● running</span>}</span>
            <span className="text-foreground/60 flex-1 truncate">{l.user_name || ""} {l.note ? `— ${l.note}` : ""}</span>
            <span className="text-[10px] text-foreground/40">{formatISTDate(l.started_at)}</span>
            {canEdit && l.stopped_at && (
              <button onClick={() => del(l.id)} className="text-rose-400/70 hover:text-rose-300" data-testid={`time-del-${l.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export function RecurrenceSection({ taskId, canEdit, currentRecurrence, onSaved }) {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(currentRecurrence || null);
  const [preview, setPreview] = useState([]);

  useEffect(() => { setCfg(currentRecurrence || null); }, [currentRecurrence]);

  const save = async (next) => {
    setCfg(next);
    try {
      await api.patch(`/pm/tasks/${taskId}`, { recurrence: next });
      const { data } = await api.get(`/pm/tasks/${taskId}/recurrence-preview?count=5`);
      setPreview(data.occurrences || []);
      toast.success(t("recurring.saved", "Recurrence saved"));
      onSaved?.();
    } catch (e) {
      errorToast(e);
    }
  };

  useEffect(() => {
    if (cfg && taskId && taskId !== "new") {
      api.get(`/pm/tasks/${taskId}/recurrence-preview?count=5`).then(r => setPreview(r.data.occurrences || [])).catch(() => {});
    } else { setPreview([]); }
  }, [cfg?.frequency, cfg?.interval, JSON.stringify(cfg?.weekdays || []), taskId]);

  if (!taskId || taskId === "new") return null;

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="recurrence-section">
      <div className="flex items-center gap-2 mb-2">
        <Repeat className="w-3.5 h-3.5 text-sky-300" />
        <span className="text-xs uppercase tracking-widest text-foreground/70">{t("recurring.title", "Repeat")}</span>
      </div>
      <div className="flex gap-1 mb-2 flex-wrap">
        {[
          { k: null, l: t("recurring.off", "Off") },
          { k: "daily", l: t("recurring.daily", "Daily") },
          { k: "weekly", l: t("recurring.weekly", "Weekly") },
          { k: "monthly", l: t("recurring.monthly", "Monthly") },
        ].map(o => (
          <button key={String(o.k)} disabled={!canEdit}
            onClick={() => save(o.k ? { frequency: o.k, interval: 1, weekdays: o.k === "weekly" ? [new Date().getDay()] : undefined, ends: { type: "never" }, spawned_count: 0 } : null)}
            data-testid={`recur-freq-${o.k || "off"}`}
            className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${cfg?.frequency === o.k || (!cfg && !o.k) ? "bg-sky-500/20 border-sky-500 text-sky-200" : "border-white/10 text-foreground/60"}`}>
            {o.l}
          </button>
        ))}
      </div>
      {cfg?.frequency === "weekly" && (
        <div className="flex gap-1 mb-2" data-testid="recur-weekdays">
          {WEEKDAYS.map((w, i) => {
            const on = (cfg.weekdays || []).includes(i);
            return (
              <button key={i} disabled={!canEdit}
                onClick={() => save({ ...cfg, weekdays: on ? (cfg.weekdays || []).filter(x => x !== i) : [...(cfg.weekdays || []), i] })}
                data-testid={`recur-wd-${i}`}
                className={`w-6 h-6 rounded-full text-[10px] ${on ? "bg-sky-500 text-white" : "bg-white/5 text-foreground/50 border border-white/10"}`}>
                {w}
              </button>
            );
          })}
        </div>
      )}
      {cfg && (
        <div className="flex gap-2 items-center mb-2 text-xs" data-testid="recur-ends">
          <span className="text-foreground/60">{t("recurring.ends", "Ends:")}</span>
          <Select value={cfg.ends?.type || "never"} onValueChange={v => save({ ...cfg, ends: { ...cfg.ends, type: v } })} disabled={!canEdit}>
            <SelectTrigger className="h-7 w-32 text-xs" data-testid="recur-ends-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="never">{t("recurring.never", "Never")}</SelectItem>
              <SelectItem value="on">{t("recurring.on", "On date")}</SelectItem>
              <SelectItem value="after_count">{t("recurring.after_n", "After N")}</SelectItem>
            </SelectContent>
          </Select>
          {cfg.ends?.type === "on" && (
            <Input type="date" value={(cfg.ends.on_date || "").slice(0, 10)}
              onChange={e => save({ ...cfg, ends: { ...cfg.ends, on_date: new Date(e.target.value).toISOString() } })}
              disabled={!canEdit} className="h-7 text-xs w-36" data-testid="recur-ends-date" />
          )}
          {cfg.ends?.type === "after_count" && (
            <Input type="number" min={1} max={100} value={cfg.ends.count || 4}
              onChange={e => save({ ...cfg, ends: { ...cfg.ends, count: parseInt(e.target.value) || 1 } })}
              disabled={!canEdit} className="h-7 text-xs w-16" data-testid="recur-ends-count" />
          )}
        </div>
      )}
      {preview.length > 0 && (
        <div className="text-[10px] text-foreground/50 pt-2 border-t border-white/5" data-testid="recur-preview">
          {t("recurring.next5", "Next 5:")} {preview.map(o => formatISTShort(o)).join(" · ")}
        </div>
      )}
      {cfg?.spawned_count > 0 && (
        <div className="text-[10px] text-emerald-300/80 pt-1">
          {t("recurring.spawned", "Spawned:")} {cfg.spawned_count}
        </div>
      )}
    </div>
  );
}

/** Global running-timer chip for the top nav bar. */
export function GlobalTimerChip() {
  const { t } = useTranslation();
  const [active, setActive] = useState(null);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/pm/me/active-timer");
      setActive(data.active || null);
    } catch (e) { setActive(null); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const t2 = setInterval(() => setTick(x => x + 1), 60000);
    return () => clearInterval(t2);
  }, []);
  if (!active) return null;
  const started = new Date(active.started_at);
  const mins = Math.max(0, Math.floor((Date.now() - started.getTime()) / 60000));
  return (
    <a href={`/projects/${active.project_id || ""}`}
      onClick={(e) => { e.preventDefault(); if (active.project_id) window.location.assign(`/projects/${active.project_id}`); }}
      data-testid="global-timer-chip"
      className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-[11px] text-emerald-200 shadow-[0_0_12px_rgba(30,150,252,0.15)] animate-pulse-slow">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      <Clock className="w-3 h-3" />
      <span className="truncate max-w-[140px]">{active.task_title || t("time.running", "Running")}</span>
      <span className="font-medium">{fmtDur(mins)}</span>
    </a>
  );
}
